/* eslint-disable @typescript-eslint/no-explicit-any */
import { stripHtmlTags, toastWarning } from "helpers";
import { PasteOptionsRequest } from "providers";
import Quill, { Delta } from "quill";

import { logger } from "../index";
import { getRichTextQuillOptions } from "../richTextQuillOptions";

/**
 * Configures clipboard behaviour for a specific Quill instance:
 *
 * 1. Strips image nodes from every pasted Delta via addMatcher.
 * 2. When the clipboard contains HTML, immediately inserts the HTML delta (default).
 *    If the clipboard also contains plain text, calls `showPasteOptions` so the user
 *    can switch to plain text or revert — without any blocking dialog.
 * 3. When the clipboard contains only plain text, inserts it directly.
 */
export function setupQuillClipboard(quill: Quill, showPasteOptions: (req: PasteOptionsRequest) => void): void {
  // 1. Strip <img> blots from HTML-converted deltas
  quill.clipboard.addMatcher("IMG", () => new (Delta as any)());

  // 2. Capture-phase paste listener (runs before Quill's own handler)
  quill.root.addEventListener(
    "paste",
    (e: ClipboardEvent) => {
      const clipboard = e.clipboardData;
      if (!clipboard) return;

      const files = clipboard.files;

      const textHtml = clipboard.getData("text/html");
      let textPlain = clipboard.getData("text/plain");

      const convertedHtml = textHtml ? quill.clipboard.convert({ html: textHtml }) : new (Delta as any)();
      const htmlDisplayValue = getHtmlDisplayValue(convertedHtml);

      if (!textPlain) {
        textPlain = textPlain || stripHtmlTags(textHtml);
        logger.debug("quill.paste(): textPlain is empty, using stripped html", files);
      }

      if (files && files.length > 0 && !textPlain && !htmlDisplayValue) {
        logger.debug("quill.paste(): files:=", files);

        e.preventDefault();
        e.stopPropagation();
        toastWarning("Pasted content contains files but no text to paste");
        return;
      }

      if (!textPlain && !htmlDisplayValue) {
        logger.debug("quill.paste(): nothing to paste", files);
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Snapshot before paste so we can always revert
      const preContents = quill.getContents();
      const preSelection = quill.getSelection();

      if (htmlDisplayValue) {
        // Apply HTML immediately (default — like Word's "Keep Source Formatting")
        insertHtml(quill, convertedHtml);
      } else {
        insertText(quill, textPlain);
      }

      const anchorRect = getAnchorRect(quill);

      showPasteOptions({
        anchorRect,
        onPlainText: htmlDisplayValue
          ? () => {
              // Revert HTML paste, insert plain text instead
              quill.setContents(preContents, "silent");
              if (preSelection) quill.setSelection(preSelection.index, preSelection.length, "silent");
              insertText(quill, textPlain);
            }
          : undefined,
        onCancel: () => {
          // Revert to pre-paste state entirely
          quill.setContents(preContents, "silent");
          if (preSelection) quill.setSelection(preSelection.index, preSelection.length, "silent");
        },
      });
    },
    true // capture phase — fires before Quill's own paste listener
  );
}

function getAnchorRect(quill: Quill): DOMRect {
  const selection = quill.getSelection();
  if (selection) {
    try {
      // Use the last inserted character (index - 1) to get its right edge.
      // When the cursor lands at the start of a new line (left=0), this correctly
      // positions the widget to the right of the actual content on the previous line.
      const boundsIndex = selection.index > 0 ? selection.index - 1 : selection.index;
      const bounds = quill.getBounds(boundsIndex);
      if (bounds) {
        const containerRect = quill.root.getBoundingClientRect();
        return new DOMRect(
          containerRect.left + bounds.right,
          containerRect.top + bounds.top,
          0,
          bounds.bottom - bounds.top
        );
      }
    } catch {
      // fall through to container fallback
    }
  }
  return quill.root.getBoundingClientRect();
}

function insertHtml(quill: Quill, delta: Delta): void {
  const range = quill.getSelection(true);
  const index = range ? range.index : quill.getLength();
  const length = range ? range.length : 0;
  quill.updateContents(new (Delta as any)().retain(index).delete(length).concat(delta), "user");
  quill.setSelection(index + delta.length(), 0, "silent");
}

function insertText(quill: Quill, text: string): void {
  const range = quill.getSelection(true);
  const index = range ? range.index : quill.getLength();
  const length = range ? range.length : 0;
  quill.deleteText(index, length, "user");
  quill.insertText(index, text, "user");
  quill.setSelection(index + text.length, 0, "silent");
}

function getHtmlDisplayValue(delta: Delta): string {
  if (!delta.length()) return "";

  const container = document.createElement("div");
  const preview = new Quill(container, getRichTextQuillOptions());
  preview.setContents(delta);

  return preview
    .getSemanticHTML()
    .replace(/&nbsp;/gi, " ")
    .trim();
}
